import React, { useState, ChangeEvent, useContext, useEffect, useRef, KeyboardEvent } from 'react';
import zoomContext from "../../Contexts/zoom-context";
import { 
    CloseOutlined ,
} from '@ant-design/icons';

interface Message {
  text: string;
  sender?: string;
  timestamp: number;
}

interface ChatScreenProps {
  toggleChat: () => void;
  isChatOpen:any
  setIsChatOpen: any
}

const ChatInterface: React.FC<ChatScreenProps> = ({ toggleChat, isChatOpen, setIsChatOpen }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [recipientData, setRecipientData] = useState<any>([])
  const [recipient, setRecipient ] = useState<any>()
  const [selectedRecipientName, setSelectedRecipientName] = useState<string>('')
  const [inputMessage, setInputMessage] = useState<string>('');
  const client = useContext(zoomContext);
  const chat = client?.getChatClient();
  const [userIdentity, setUserIdentity] = useState<string>('');
  const chatBodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Fetch chat history when the component mounts or when chat is toggled open
    const loadChatHistory = async () => {
      if (chat) {
        try {
          const history = await chat.getHistory();
          console.log(history)
          const loadedMessages: Message[] = history.map((msg: any) => ({
            text: msg.message,
            sender: msg.sender.name,
            timestamp: msg.timestamp,
          }));
          setMessages(loadedMessages);
        } catch (error) {
          console.error('Error loading chat history:', error);
        }
      }
    };

    loadChatHistory();
  }, [chat]);

  useEffect(() => {
    const user = client?.getCurrentUserInfo(); // Adjust based on your SDK's API
    const currentUser = user?.userId;

    if (currentUser) {
      // Set user identity
      const allParticipants = client?.getAllUser();
      const currentUserParticipant = allParticipants?.find(participant => participant.userId === currentUser);
      if (currentUserParticipant) {
        setUserIdentity(currentUserParticipant.displayName);
      }
    }

    const handleChatMessage = (payload: any) => {
      setIsChatOpen(true)
      const senderId = payload.sender.userId;

      // Only add the message if it's not from the current user
      if (senderId !== currentUser) {
        const newMessage: Message = {
          text: payload.message,
          sender: payload.sender.displayName,
          timestamp: Date.now()
        };
        setMessages(prevMessages => [...prevMessages, newMessage]);
      }
    };

    client?.on('chat-on-message', handleChatMessage);

    // Clean up listener when the component unmounts
    return () => {
      client?.off('chat-on-message', handleChatMessage);
    };
  }, [client, setIsChatOpen]);

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      const userMessage: Message = {
        text: inputMessage,
        sender: userIdentity,
        timestamp: Date.now()
      };
      setMessages(prevMessages => [...prevMessages, userMessage]);

      if (recipient && recipient !== 'general') {
        chat?.send(inputMessage, Number(recipient));
      } else {
        chat?.sendToAll(inputMessage); 
      }
      setInputMessage('')
      if (chatBodyRef.current) {
        chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
      }
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputMessage(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };
  useEffect (() => {
    const allParticipants = client?.getAllUser()
    setRecipientData(allParticipants)
  }, [client])

  const handleRecipientChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedRecipientID = e.target.value;
    setRecipient(selectedRecipientID)
    const name = recipientData.find((recipient: any) => recipient.userId === Number(selectedRecipientID))
    console.log(name)

    if (name) {
      setSelectedRecipientName(name.displayName)
    } else {
      setSelectedRecipientName('General Chat')
    }

  }

  return (
    <div style={styles.chatContainer}>
      <div style={styles.header}>
        <div className='flex flex-col justify-center align-center'>
            <p>{recipient ? `${selectedRecipientName}` : 'General Chat'}</p>
            <select
                name="recipient"
                id="recipient"
                value={recipient}
                className="vc-reg-input text-black"
                onChange={handleRecipientChange}>
                  <option value="general" >General Chat</option>
                  {recipientData && recipientData.map((recipient: any) => (
                    <option key={recipient.userId} value={recipient.userId}>
                      {recipient.displayName}
                    </option>
                  ))}
            </select>
        </div>
        
        <div style={styles.chatOptions} onClick={toggleChat}>
          <CloseOutlined />
        </div>
      </div>
      <div style={styles.chatBody} ref={chatBodyRef}>
        {messages.map((message, index) => (
          <div
            key={index}
            style={{
              ...styles.messageContainer,
              justifyContent: message.sender === userIdentity ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                ...styles.messageBubble,
                backgroundColor: message.sender === userIdentity ? '#E3E3E3' : '#4B2A18',
                color: message.sender === userIdentity ? '#000' : '#FFF',
              }}
              className='flex flex-col'
            >
              {message.sender !== userIdentity && <span style={{ fontSize: '0.8em', color: '#fff' }}>{message.sender}:</span>}  
              {message.text}
              <span style={{ fontSize: '0.6em', color: '#888' }}>
                {new Date(message.timestamp).toLocaleTimeString()}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div style={styles.inputContainer}>
            <input
            type="text"
            value={inputMessage}
            placeholder="Type your message..."
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            style={styles.input}
            />    
            <button onClick={handleSendMessage} style={styles.sendButton}>➤</button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chatContainer: {
    position: 'absolute',
    bottom: '0px',
    right: '20px',
    width: '300px',
    height: '400px',
    border: '1px solid #ccc',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFF',
    overflow: 'hidden',
    zIndex: 1,
    fontFamily: 'Arial, sans-serif',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
  },
  header: {
    backgroundColor: '#4B2A18',
    color: '#FFF',
    padding: '15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chatOptions: {
    cursor: 'pointer',
  },
  chatBody: {
    flex: 1,
    padding: '10px',
    overflowY: 'scroll',
  },
  messageContainer: {
    display: 'flex',
    marginBottom: '10px',
  },
  messageBubble: {
    maxWidth: '60%',
    padding: '10px',
    borderRadius: '10px',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    borderTop: '1px solid #ccc',
  },
  input: {
    flex: 1,
    padding: '10px',
    border: 'none',
    borderRadius: '20px',
    backgroundColor: '#F1F1F1',
    outline: 'none',
  },
  sendButton: {
    marginLeft: '10px',
    padding: '10px',
    border: 'none',
    backgroundColor: '#4B2A18',
    color: '#FFF',
    borderRadius: '50%',
    cursor: 'pointer',
    outline: 'none',
  },
};

export default ChatInterface;
